import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Building2, MapPin, Calendar, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { companyInfo } from '../data/mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About SALAR Technologies LLC | Founder-Led Engineering Platform</title>
        <meta name="description" content="SALAR Technologies LLC — founder-led, patent-backed engineering platform for electronics reliability, semiconductor supply-chain visibility, provenance assurance, manufacturing stability, AI infrastructure orchestration, post-quantum trust provenance, high-speed validation, and fault-tolerant quantum hardware assurance. Founded by Vikrant Salar, former Intel Senior TPM with ten patent filings." />
        <meta property="og:title" content="About SALAR Technologies LLC" />
        <meta property="og:description" content="Founder-led engineering platform for resilient electronics. Oregon LLC. Patent-backed. U.S.-based. Founded by 21-year semiconductor industry veteran." />
        <meta property="og:url" content="https://www.salartechnologies.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/about-salar-circuit-board.jpg" />
        <meta property="og:image:alt" content="Advanced circuit board assembly representing SALAR Technologies semiconductor and electronics engineering" />
        <link rel="canonical" href="https://www.salartechnologies.com/about" />
        <link rel="preload" as="image" href="/img/about-salar-circuit-board.jpg" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="relative text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.9)), url('/img/about-salar-circuit-board.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center 48%',
        }}
        data-testid="about-hero"
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">About SALAR Technologies LLC</h1>
            <p className="text-xl text-blue-200">
              Engineering-first capability development for mission-critical electronics and resilient systems.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-blue-100 leading-relaxed mb-6">
                SALAR Technologies LLC is a founder-led engineering and research platform built to address one 
                of the hardest problems in modern strategic industry: how to improve resilience, trust, and 
                readiness across complex electronics and semiconductor systems.
              </p>
              
              <p className="text-lg text-blue-100 leading-relaxed mb-6">
                The company is structured around engineering-first, research-first capability development in 
                areas including electronics reliability, predictive failure analysis, semiconductor supply-chain 
                visibility, provenance and counterfeit-risk control, and manufacturing stability.
              </p>
              
              <p className="text-lg text-blue-100 leading-relaxed mb-6">
                SALAR is not positioned as a generic consulting company or a generic AI brand. It is built as 
                a technically serious platform for public-safe capability development, government and partnership 
                positioning, and future product-oriented engineering tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Profile */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Company Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-blue-500/30 bg-blue-900/30 backdrop-blur-sm">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                  <Building2 className="text-white" size={24} />
                </div>
                <CardTitle className="text-white">Business Structure</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-blue-100">
                  <li><strong>Entity Type:</strong> Oregon LLC</li>
                  <li><strong>Registry No.:</strong> {companyInfo.registryNumber}</li>
                  <li><strong>Status:</strong> Active</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-blue-500/30 bg-blue-900/30 backdrop-blur-sm">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                  <Calendar className="text-white" size={24} />
                </div>
                <CardTitle className="text-white">Formation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-blue-100">
                  <li><strong>Founder-led:</strong> Yes</li>
                  <li><strong>Patent-backed:</strong> Yes</li>
                  <li><strong>U.S.-based:</strong> Yes</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-blue-500/30 bg-blue-900/30 backdrop-blur-sm">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                  <MapPin className="text-white" size={24} />
                </div>
                <CardTitle className="text-white">Location</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-blue-100">
                  <li><strong>Headquarters:</strong> {companyInfo.location}</li>
                  <li><strong>State:</strong> {companyInfo.state}</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-blue-500/30 bg-blue-900/30 backdrop-blur-sm">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                  <FileText className="text-white" size={24} />
                </div>
                <CardTitle className="text-white">Positioning</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-blue-100">
                  <li>Engineering-first</li>
                  <li>Research-first</li>
                  <li>Defense-adjacent</li>
                  <li>Government-ready</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section
        className="py-16 backdrop-blur-sm"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.7), rgba(10,22,40,0.9)), url('/img/server_room_close.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="about-mission-section"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Public Mission</h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              To convert fragmented engineering, supplier, pedigree, manufacturing, and field signals into 
              actionable readiness and resilience decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-16 bg-blue-950/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Founder</h2>
            <Card className="border-blue-500/30 bg-blue-900/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Vikrant Salar</CardTitle>
                <p className="text-cyan-400 font-semibold">Founder & Principal</p>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-blue-100 leading-relaxed">
                  Senior Technical Program Manager with <strong>21 years of engineering experience</strong> across 
                  SoC/ASIC design, broadband-access and mobile-backhaul systems, hardware reliability, supply-chain risk analytics, and advanced semiconductor manufacturing. Earlier work at Nokia / Nokia Siemens Networks (NSN) included the D50 and D500 DSLAM platforms and Agora mobile-backhaul router programs. 
                  Most recently <strong>Senior TPM at Intel</strong>. Founder of SALAR Technologies LLC. 
                  Holds <strong>ten patent filings</strong> spanning board-level reliability governance, profile-based Ethernet connectivity,
                  semiconductor supply-chain graph intelligence, closed-loop manufacturing stability, public-safe lithography/process
                  assurance, reliability-aware AI infrastructure power/thermal orchestration, post-quantum cryptographic provenance,
                  agentic action governance, high-speed validation alignment, and fault-tolerance-indexed quantum hardware assurance.
                </p>
                <a href="/vikrant-salar" className="mt-6 inline-flex rounded-lg border border-cyan-400/40 px-5 py-3 font-semibold text-cyan-200 transition hover:bg-cyan-500/15">View full Founder &amp; Principal profile</a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Focus Areas */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Core Focus Areas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Electronics Reliability',
                'Semiconductor Supply-Chain Visibility',
                'Provenance & Counterfeit-Risk Control',
                'Manufacturing Stability',
                'Predictive Failure Analytics',
                'Explainable Technical Decision Support'
              ].map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-3 p-4 bg-blue-900/30 rounded-lg border border-blue-500/30"
                >
                  <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 font-medium">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
